/**
 * Portal-rendered popover menu.
 *
 * WHY: Dropdowns positioned with `absolute` are clipped by any ancestor with
 * ``overflow: hidden|auto|clip`` — which is most of our sidebars, tab panels
 * and scroll containers. Rendering the menu into ``document.body`` via a
 * React portal escapes every ancestor and is the only dependable fix for
 * "my kebab menu gets cut off" bugs. See
 * ``.cursor/rules/frontend-dropdown-portal.mdc``.
 *
 * USAGE:
 *   const anchorRef = useRef(null);
 *   const [open, setOpen] = useState(false);
 *   <button ref={anchorRef} onClick={() => setOpen(v => !v)} />
 *   <PortalMenu anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
 *     <ul> ... menu items ... </ul>
 *   </PortalMenu>
 *
 * POSITIONING:
 * - Default alignment is ``bottom-right`` (right edge of anchor, below).
 * - ``top-left`` / ``top-right`` pin the menu **above** the anchor (rail
 *   settings gear); if there isn't room above, falls back to below.
 * - Re-measures on open + on scroll/resize + when the menu content changes
 *   size (ResizeObserver).
 * - Clamps into viewport; flips to above the anchor if there isn't room
 *   below (bottom-* alignments only).
 *
 * A11Y:
 * - Outside-click and ``Escape`` close the menu.
 * - ``role="menu"`` on the popover; callers supply menu items / roles.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_PAD = 8;

export function PortalMenu({
  anchorRef,
  open,
  onClose,
  children,
  align = 'bottom-right',
  offset = 4,
  className = 'rounded-lg border border-gray-200 bg-white shadow-lg',
  width,
  menuRole = 'menu',
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menu = menuRef.current;
    const menuW = menu ? menu.offsetWidth : width || 0;
    const menuH = menu ? menu.offsetHeight : 0;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (align === 'top-left' || align === 'top-right') {
      let top = rect.top - offset - menuH;
      let left = align === 'top-left' ? rect.left : rect.right - menuW;
      if (menuH > 0 && top < VIEWPORT_PAD) {
        top = rect.bottom + offset;
      }
      if (left + menuW > vw - VIEWPORT_PAD) left = vw - VIEWPORT_PAD - menuW;
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
      if (top + menuH > vh - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, vh - VIEWPORT_PAD - menuH);
      }
      setPos({ top, left, ready: true });
      return;
    }

    let top = rect.bottom + offset;
    let left = align === 'bottom-left' ? rect.left : rect.right - menuW;

    if (left + menuW > vw - VIEWPORT_PAD) left = vw - VIEWPORT_PAD - menuW;
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
    if (top + menuH > vh - VIEWPORT_PAD) {
      const above = rect.top - offset - menuH;
      if (above >= VIEWPORT_PAD) top = above;
      else top = Math.max(VIEWPORT_PAD, vh - VIEWPORT_PAD - menuH);
    }

    setPos({ top, left, ready: true });
  }, [anchorRef, align, offset, width]);

  useLayoutEffect(() => {
    if (!open) {
      setPos((p) => ({ ...p, ready: false }));
      return undefined;
    }
    updatePosition();
    const handle = () => updatePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    let resizeObs;
    if (menuRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObs = new ResizeObserver(() => updatePosition());
      resizeObs.observe(menuRef.current);
    }
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
      if (resizeObs) resizeObs.disconnect();
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={menuRef}
      role={menuRole}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 1000,
        width,
        visibility: pos.ready ? 'visible' : 'hidden',
      }}
      className={className}
    >
      {children}
    </div>,
    document.body,
  );
}

export default PortalMenu;
